import { useSourceCodeStore } from '@kaoto/kaoto';
import { useSyncExternalStore } from 'react';

import { setCanvasLevel } from './canvas-level';

/** Which Casa route the canvas is currently showing, so the anchors can say whose it is. */
export interface CanvasSubject {
  routeId: string;
  routeName: string;
  vendorId: string;
  groupId: string;
  groupName: string;
  scope: string;
  flowPath: string;
}

let subject: CanvasSubject | undefined;
const listeners = new Set<() => void>();

const notify = (): void => {
  listeners.forEach((listener) => {
    listener();
  });
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);

  return () => listeners.delete(listener);
};

export const canvasSubject = (): CanvasSubject | undefined => subject;

export const clearCanvasSubject = (): void => {
  subject = undefined;
  notify();
};

/** Reads the canvas subject, re-rendering when a different route is opened. */
export const useCanvasSubject = (): CanvasSubject | undefined =>
  useSyncExternalStore(subscribe, canvasSubject, canvasSubject);

/**
 * Hands a Casa route to Kaoto proper, showing the Camel route the configuration describes.
 *
 * The flow is compiled from the configuration rather than read from a stored file, so the
 * canvas cannot drift from what the studio holds: change the sink or the ingress in the studio
 * and the canvas shows it the next time it is opened.
 */
export const openOnCanvas = async (next: CanvasSubject, flow: string): Promise<void> => {
  subject = next;
  setCanvasLevel({
    kind: 'route',
    vendorId: next.vendorId,
    groupId: next.groupId,
    routeId: next.routeId,
    scope: next.scope,
  });
  notify();
  useSourceCodeStore.getState().setCodeAndNotify(flow, next.flowPath);
};

/**
 * What the canvas currently holds, for reading an edit back into the configuration. Kaoto keeps
 * the open document in its own store, so this is the single place that reads it.
 */
export const canvasSource = (): string => useSourceCodeStore.getState().sourceCode;
